import React from "react"
import {
  AlertTriangle,
  BarChart2,
  Clock,
  Download,
  Loader2,
  Play,
  RotateCcw,
  Trash2,
} from "lucide-react"
import { paperDisplayTitle, type PaperSession } from "../types"
import {
  formatPaperDate,
  formatPaperElapsed,
} from "../utils/formatPaperTime"
import { progressOf, scoreOf, awaitingMarks, PAPER_STATUS_LABEL, generationProgressOf } from "../utils/paperStatus"
import { scoreBand } from "../utils/scoreThreshold"
import "./SessionCard.style.scss"

export const StatusBadge = ({ status }: { status: PaperSession["status"] }) => (
  <span className={`paper-status-badge is-${status}`}>
    {PAPER_STATUS_LABEL[status]}
  </span>
)

interface SessionCardProps {
  session: PaperSession
  isLaunching?: boolean
  pdfLoading?: boolean
  onResume: () => void
  onViewAnalytics: () => void
  onRetake: () => void
  onDownloadPdf: () => void
  onDelete: () => void
}

const SessionCard = ({
  session,
  isLaunching = false,
  pdfLoading = false,
  onResume,
  onViewAnalytics,
  onRetake,
  onDownloadPdf,
  onDelete,
}: SessionCardProps) => {
  const { answered, total, pct } = progressOf(session)
  const generating = generationProgressOf(session)
  const { score, max, pct: scorePct, isPending } = scoreOf(session)
  const pendingMarks = awaitingMarks(session)
  const band = scoreBand(scorePct)
  const showScore =
    session.status === "marked" ||
    (session.status === "submitted" && !isPending)
  const startLabel = session.status === "not_started" ? "Start" : "Resume"
  const isPreparing = session.status === "preparing"
  const canDownloadPdf =
    session.status !== "preparing" && session.status !== "failed"
  const displayTitle = paperDisplayTitle(session)
  const showSubject =
    Boolean(session.subjectTitle?.trim()) &&
    displayTitle !== session.subjectTitle.trim()

  return (
    <article
      className={`session-card ${
        isPreparing
          ? "is-preparing"
          : session.status === "in_progress"
            ? "is-progress"
            : session.status === "abandoned" || session.status === "failed"
              ? "is-abandoned"
              : ""
      }${isLaunching ? " is-launching" : ""}`}
    >
      <header className="session-card__header">
        <div className="session-card__title-row">
          <div className="session-card__titles">
            <span className="session-card__heading">{displayTitle}</span>
            {showSubject && (
              <span className="session-card__subject">
                {session.subjectTitle}
              </span>
            )}
          </div>
          <StatusBadge status={session.status} />
        </div>
        <span className="session-card__date">
          {formatPaperDate(session.startedAt || session.lastActiveAt)}
        </span>
      </header>

      <div className="session-card__body">
        {isPreparing && (
          <div className="session-card__meter">
            <div className="session-card__meter-labels">
              <span>
                <Loader2 size={14} className="is-spinning" />
                {generating.filled} / {generating.total} questions ready
              </span>
              <span>{generating.pct}%</span>
            </div>
            <div className="session-card__track">
              <div
                className="session-card__fill is-in_progress"
                style={{ width: `${generating.pct}%` }}
              />
            </div>
          </div>
        )}

        {!showScore && !isPreparing && (
          <div className="session-card__meter">
            <div className="session-card__meter-labels">
              <span>
                {answered} / {total} questions answered
              </span>
              <span>{pct}%</span>
            </div>
            <div className="session-card__track">
              <div
                className={`session-card__fill is-${session.status}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {showScore && (
          <div className="session-card__meter">
            <div className="session-card__meter-labels">
              <span>
                Score: {score} / {max} marks
              </span>
              <span className={`session-card__score-pct is-${band}`}>
                {scorePct}%
              </span>
            </div>
            <div className="session-card__track">
              <div
                className={`session-card__fill is-score-${band}`}
                style={{ width: `${scorePct}%` }}
              />
            </div>
          </div>
        )}

        <div className="session-card__meta">
          {isPreparing && (
            <span>
              <Loader2 size={14} className="is-spinning" />
              Generating questions…
            </span>
          )}
          {!isPreparing && (
            <span>
              <Clock size={14} />
              {formatPaperElapsed(session.totalTimeSpentSeconds)} elapsed
            </span>
          )}
          <span>{total} questions</span>
          {session.status === "failed" && (
            <span className="session-card__warn">
              <AlertTriangle size={14} />
              Generation failed
            </span>
          )}
          {pendingMarks && (
            <span className="session-card__warn">Awaiting marks</span>
          )}
          {session.assessmentStatus === "failed" && (
            <span className="session-card__warn">
              <AlertTriangle size={14} />
              {session.assessmentError || "AI marking failed"}
            </span>
          )}
        </div>
      </div>

      <footer className="session-card__footer">
        {(session.status === "in_progress" ||
          session.status === "not_started") && (
          <button
            type="button"
            className="is-primary"
            onClick={onResume}
            disabled={isLaunching}
            aria-busy={isLaunching}
          >
            {isLaunching ? (
              <>
                <Loader2 size={14} className="is-spinning" /> Opening…
              </>
            ) : (
              <>
                <Play size={14} />
                {startLabel}
              </>
            )}
          </button>
        )}
        {(session.status === "marked" || session.status === "submitted") && (
          <button
            type="button"
            className="is-soft"
            onClick={onViewAnalytics}
            disabled={isLaunching}
          >
            <BarChart2 size={14} />
            {isPending ? "Review" : "Analytics"}
          </button>
        )}
        {(session.status === "abandoned" ||
          session.status === "marked" ||
          (session.status === "submitted" && !pendingMarks)) && (
          <button
            type="button"
            className="is-ghost"
            onClick={onRetake}
            disabled={isLaunching}
            aria-busy={isLaunching}
          >
            {isLaunching ? (
              <>
                <Loader2 size={14} className="is-spinning" /> Opening…
              </>
            ) : (
              <>
                <RotateCcw size={14} />
                Retake
              </>
            )}
          </button>
        )}
        {canDownloadPdf && (
          <button
            type="button"
            className="is-ghost"
            onClick={onDownloadPdf}
            disabled={isLaunching || pdfLoading}
            aria-busy={pdfLoading}
          >
            {pdfLoading ? (
              <>
                <Loader2 size={14} className="is-spinning" /> Preparing PDF…
              </>
            ) : (
              <>
                <Download size={14} />
                Download PDF
              </>
            )}
          </button>
        )}
        <button
          type="button"
          className="session-card__delete"
          onClick={onDelete}
          disabled={isLaunching}
          aria-label="Delete session"
        >
          <Trash2 size={14} />
        </button>
      </footer>
    </article>
  )
}

export default SessionCard
